import { environment } from 'src/environments/environment';
import { BUILD_INFO } from 'src/environments/build-info';

/**
 * MODO DEBUG — `window.mpDebug` (Marlon 02/10/2026: "comandos de debug mode no frontend para fazer
 * tweaks de testes A/B e outras configurações sem ter que ficar definindo coisas no banco").
 *
 * Força, NESTE aparelho, os braços dos A/B da tela de endereço e alguns números de configuração,
 * sem SQL nem cardapio.json. Cada override é lido no PONTO REAL em que a coisa é decidida
 * (`reverseConfirmArm`, `earlyConfirmArm`, `geoProviderArm`, `effectiveRolloutPercent`, a guarda de
 * bairro, o corte da A4a) — a tela roda o código de produção; só o número de entrada muda.
 *
 * Onde existe: build não-produção, localhost, e os ambientes de teste (blue/green/yellow). Em
 * produção (`pedir.delivery`, domínios de loja) NÃO existe: nem o objeto global, nem a leitura —
 * um override gravado à mão no localStorage de um cliente não tem efeito nenhum lá.
 *
 * Toda telemetria emitida com override ativo leva `debug_overrides` (ver `withDebugOverrides`),
 * para as leituras dos A/B excluírem essas sessões.
 */

export const DEBUG_STORAGE_KEY = 'mp_debug_overrides';
export const DEBUG_STORAGE_VERSION = 1;
const AMBIENTES_DE_TESTE = ['blue', 'green', 'yellow'];

export type DebugKey = 'a4a' | 'a4aMinMovedM' | 'a4b' | 't20' | 'dial' | 'zoneGuard' | 'geoProvider' | 'overlay';

interface DebugSpec {
    descricao: string;
    exemplo: string;
    /** Valor normalizado, ou `undefined` = inválido (o `set` recusa). */
    normalizar: (v: any) => any;
}

const escolha = (...ok: string[]) => (v: any) => {
    const s = String(v ?? '').trim().toLowerCase();
    return ok.includes(s) ? s : undefined;
};
const numero = (min: number, max: number) => (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= min && n <= max ? n : undefined;
};

export const DEBUG_SPEC: Record<DebugKey, DebugSpec> = {
    a4a: {
        descricao: 'A/B A4a (busca do endereço do pino só no Confirmar): força o braço deste aparelho. off = fora do experimento',
        exemplo: "mpDebug.set('a4a', 'confirmar')   // ou 'arraste' | 'off'",
        normalizar: escolha('confirmar', 'arraste', 'off'),
    },
    a4aMinMovedM: {
        descricao: 'A4a: distância (m) do ponto conhecido a partir da qual o Confirmar busca o endereço (produção: 100)',
        exemplo: "mpDebug.set('a4aMinMovedM', 30)",
        normalizar: numero(0, 5000),
    },
    a4b: {
        descricao: 'A4b (pino de graça no bairro travado sem mpboy): força o braço numa loja do segmento. gratis = pino por GPS/Photon/bairro e sem reverse; pago = a tela de sempre; off = fora',
        exemplo: "mpDebug.set('a4b', 'gratis')   // ou 'pago' | 'off'",
        normalizar: escolha('gratis', 'pago', 'off'),
    },
    t20: {
        descricao: 'A/B T20 (confirmar o endereço no início): força o braço. Só age em cliente ELEGÍVEL (endereço salvo sem coordenada confiável)',
        exemplo: "mpDebug.set('t20', 'inicio')   // ou 'botao' | 'off'",
        normalizar: escolha('inicio', 'botao', 'off'),
    },
    dial: {
        descricao: "Dial da tela multissinal (0–100) no lugar do new_address_capture_flow_rollout_percent da loja; 'dentro' = 100, 'fora' = 0. Só vale em loja com o módulo da tela ligado; cliente sem endereço salvo entra sempre",
        exemplo: "mpDebug.set('dial', 'dentro')   // ou 'fora' | 0–100",
        normalizar: (v: any) => {
            const s = String(v ?? '').trim().toLowerCase();
            if (s === 'dentro') return 100;
            if (s === 'fora') return 0;
            return numero(0, 100)(v);
        },
    },
    zoneGuard: {
        descricao: "Guarda de bairro (!2034): 'on' = mostra a pergunta (100%), 'off' = só sombra (0%), ou percentual 0–100 (produção: 0)",
        exemplo: "mpDebug.set('zoneGuard', 'on')",
        normalizar: (v: any) => {
            const s = String(v ?? '').trim().toLowerCase();
            if (s === 'on') return 100;
            if (s === 'off') return 0;
            return numero(0, 100)(v);
        },
    },
    geoProvider: {
        descricao: "A/B de provedor (T7): força o braço 'here' ou 'google' (loja elegível). O SERVER confere o próprio dial (geo_provider_here_percent) e pode atender pelo Google mesmo assim — o painel mostra quem ATENDEU",
        exemplo: "mpDebug.set('geoProvider', 'here')   // ou 'google' | 'off'",
        normalizar: escolha('here', 'google', 'off'),
    },
    overlay: {
        descricao: 'Painel flutuante com as buscas REAIS de endereço (reverse e texto), gatilho, distância, braços e os últimos eventos da tela',
        exemplo: "mpDebug.set('overlay', 'on')",
        normalizar: escolha('on', 'off'),
    },
};

/** Flags e percentuais do cardapio.info que a tela de endereço lê (para o `help`). */
export const CARDAPIO_INFO_DA_TELA: Array<[string, string]> = [
    ['modules.new_address_capture_flow', 'módulo da tela multissinal (liga a tela na loja)'],
    ['modules.mpgeospatial_fee_flow', 'alta precisão deprecada: vale como módulo ligado + dial 100'],
    ['new_address_capture_flow_rollout_percent', 'dial da tela → override `dial`'],
    ['pin_reverse_on_confirm_percent', 'A/B A4a → override `a4a`'],
    ['early_address_confirm_percent', 'A/B T20 → override `t20`'],
    ['free_pin_start_percent', 'A4b, pino de graça no bairro travado sem mpboy (ausente/0 = desligado) → override `a4b`'],
    ['geo_provider_here_percent', 'A/B HERE × Google (T7) → override `geoProvider`'],
    ['delivery_fee_method / manual_zone_selection', 'taxa por DISTANCE/AREA/BAIRRO e bairro travado (não são A/B; não há override)'],
];

// ---------- onde vale ----------

/**
 * O modo debug pode existir aqui? Build não-produção sempre; build de produção só em host local ou
 * de ambiente de teste (blue/green/yellow — pelo host ou pelo `BUILD_INFO.dist` que o CI grava).
 */
export function debugAllowed(hostname: string, production: boolean, dist: string): boolean {
    if (!production) return true;
    const h = String(hostname || '').toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost')) return true;
    if (AMBIENTES_DE_TESTE.some((a) => h === `${a}.pedir.delivery`)) return true;
    return AMBIENTES_DE_TESTE.includes(String(dist || '').toLowerCase());
}

let _enabled: boolean | null = null;

export function debugEnabled(): boolean {
    if (_enabled === null) {
        try {
            const host = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
            _enabled = typeof window !== 'undefined' && debugAllowed(host, !!environment.production, BUILD_INFO.dist);
        } catch (e) {
            _enabled = false;
        }
    }
    return _enabled;
}

/** Só para testes: refaz a decisão de "onde vale". */
export function resetDebugEnabledForTests(value: boolean | null = null): void {
    _enabled = value;
}

// ---------- armazenamento ----------

interface DebugStore { v: number; src: 'mp-debug'; at: number; overrides: Partial<Record<DebugKey, any>> }

function lerStore(): DebugStore | null {
    try {
        const raw = window.localStorage.getItem(DEBUG_STORAGE_KEY);
        if (!raw) return null;
        const s = JSON.parse(raw);
        if (!s || s.v !== DEBUG_STORAGE_VERSION || s.src !== 'mp-debug' || typeof s.overrides !== 'object') return null;
        return s as DebugStore;
    } catch (e) {
        return null;
    }
}

function gravarStore(overrides: Partial<Record<DebugKey, any>>): void {
    try {
        if (!Object.keys(overrides).length) {
            window.localStorage.removeItem(DEBUG_STORAGE_KEY);
            return;
        }
        const s: DebugStore = { v: DEBUG_STORAGE_VERSION, src: 'mp-debug', at: Date.now(), overrides };
        window.localStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(s));
    } catch (e) { /* storage indisponível: o override só não persiste */ }
}

/** Overrides ativos e VÁLIDOS; `null` fora dos ambientes permitidos ou sem nenhum. */
export function debugOverridesActive(): Partial<Record<DebugKey, any>> | null {
    if (!debugEnabled()) return null;
    const s = lerStore();
    if (!s) return null;
    const out: Partial<Record<DebugKey, any>> = {};
    for (const k of Object.keys(s.overrides) as DebugKey[]) {
        const spec = DEBUG_SPEC[k];
        if (!spec) continue;
        const v = spec.normalizar(s.overrides[k]);
        if (v !== undefined) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
}

/** O valor forçado de uma chave, ou `undefined` (sem override, ou modo debug indisponível aqui). */
export function debugOverride<T = any>(key: DebugKey): T | undefined {
    const all = debugOverridesActive();
    return all ? (all[key] as T) : undefined;
}

/**
 * Carimba o envelope de telemetria com os overrides ativos. O `data` dos envelopes é JSON em string
 * (Parquet) — o campo entra DENTRO dele, que é onde as leituras olham. Sem override, devolve o mesmo
 * objeto (custo zero no caminho de produção).
 */
export function withDebugOverrides(envelope: any): any {
    const ov = debugOverridesActive();
    if (!ov || !envelope) return envelope;
    try {
        if (typeof envelope.data === 'string') {
            const data = JSON.parse(envelope.data);
            return { ...envelope, data: JSON.stringify({ ...data, debug_overrides: ov }) };
        }
        if (envelope.data && typeof envelope.data === 'object') {
            return { ...envelope, data: { ...envelope.data, debug_overrides: ov } };
        }
        return { ...envelope, debug_overrides: ov };
    } catch (e) {
        return envelope;
    }
}

// ---------- painel (overlay) ----------

interface Contadores { reversePedidos: number; reverseRespostas: Record<string, number>; textoPedidos: number }
const cont: Contadores = { reversePedidos: 0, reverseRespostas: {}, textoPedidos: 0 };
let ultimos: string[] = [];
let ultimoPino: any = null;
let bracos: any = {};
let ultimoAtendeu: string | null = null;

/** Pedido REAL de busca saindo do app (chamado pelo HighPrecisionAddressService). */
export function debugNoteRequest(kind: 'reverse' | 'texto' | 'pino', provider: string | null): void {
    if (!debugEnabled()) return;
    if (kind === 'reverse') cont.reversePedidos++;
    else if (kind === 'texto') cont.textoPedidos++;
    const alvo = kind === 'reverse' ? 'do endereço do pino' : kind === 'texto' ? 'do texto' : 'do pino grátis (A4b)';
    pushUltimo(`→ busca ${alvo}${provider ? ' (' + provider + ')' : ''}`);
    desenhar();
}

/** Resposta de busca: quem ATENDEU (Google/HERE), vinda do server. */
export function debugNoteServed(kind: 'reverse' | 'texto', res: any): void {
    if (!debugEnabled()) return;
    ultimoAtendeu = `${kind}: ${res?.provider ?? 'google'}${res?.provider_fallback ? ' (fallback)' : ''}`;
    desenhar();
}

/** Evento da tela, ANTES dos gates de envio (no localhost a telemetria não sai, mas o painel vê). */
export function debugObserveStep(step: string, data: any, arms: any): void {
    if (!debugEnabled()) return;
    bracos = { ...bracos, ...arms };
    if (step === 'ClientGeoReverseGeocoded') {
        const g = String(data?.reverse_trigger ?? 'sem gatilho');
        cont.reverseRespostas[g] = (cont.reverseRespostas[g] || 0) + 1;
    }
    if (step === 'ClientGeoPinMoved') ultimoPino = data;
    pushUltimo(step);
    desenhar();
}

function pushUltimo(t: string): void {
    const hora = new Date().toTimeString().slice(0, 8);
    ultimos = [`${hora} ${t}`, ...ultimos].slice(0, 8);
}

function zerarContadores(): void {
    cont.reversePedidos = 0;
    cont.reverseRespostas = {};
    cont.textoPedidos = 0;
    ultimos = [];
    ultimoPino = null;
    ultimoAtendeu = null;
}

const PAINEL_ID = 'mp-debug-overlay';
const KEY_EXPANDIDO = 'mpDebug.overlayExpandido';

function painelExpandido(): boolean {
    try { return localStorage.getItem(KEY_EXPANDIDO) === '1'; } catch (e) { return false; }
}

/**
 * Pílula compacta (Marlon 02/10: o painel grande "atrapalha para fazer teste" — cobria o mapa e o
 * botão). Recolhida por padrão: 1 linha no canto SUPERIOR ESQUERDO do mapa (ou da tela, sem mapa),
 * longe do pino central e do rodapé. Só a pílula recebe toque; tocar expande/recolhe os detalhes
 * (≤ ~6 linhas com rolagem própria, largura ≤ 60% da tela) e o estado fica no localStorage.
 */
function desenhar(): void {
    try {
        const ov = debugOverridesActive();
        let el = document.getElementById(PAINEL_ID);
        if (!ov || ov.overlay !== 'on') {
            if (el) el.remove();
            return;
        }
        if (!el) {
            el = document.createElement('div');
            el.id = PAINEL_ID;
            el.setAttribute('aria-hidden', 'true');
            el.style.cssText = 'position:fixed;z-index:2147483647;max-width:60vw;pointer-events:none;'
                + 'color:#e8edf3;font:11px/1.35 ui-monospace,Menlo,monospace;';
            el.addEventListener('click', () => {
                try { localStorage.setItem(KEY_EXPANDIDO, painelExpandido() ? '0' : '1'); } catch (e) { /* sem storage */ }
                desenhar();
            });
            document.body.appendChild(el);
        }
        const mapa = document.querySelector('app-address-map') as HTMLElement | null;
        const r = mapa ? mapa.getBoundingClientRect() : null;
        const noMapa = !!r && r.width > 40 && r.height > 40;
        el.style.left = `${Math.round((noMapa ? r!.left : 0) + 44)}px`;
        el.style.top = `${Math.round((noMapa ? r!.top : 0) + 8)}px`;

        const aberto = painelExpandido();
        const m = ultimoPino?.moved_from_known_m;
        const resumo = `A4a ${bracos.reverse_confirm_arm ?? '-'} · ${cont.reversePedidos} busca${cont.reversePedidos === 1 ? '' : 's'}`
            + (m != null ? ` · ${m} m` : '') + (aberto ? ' ▴' : ' ▾');
        const pilula = document.createElement('span');
        pilula.style.cssText = 'pointer-events:auto;cursor:pointer;display:inline-block;white-space:nowrap;'
            + 'background:rgba(16,22,30,.72);border-radius:10px;padding:2px 8px;';
        pilula.textContent = resumo;
        el.replaceChildren(pilula);
        if (!aberto) return;

        const resp = Object.entries(cont.reverseRespostas).map(([k, v]) => `${k}:${v}`).join(' ') || '-';
        const det = document.createElement('div');
        det.style.cssText = 'pointer-events:auto;margin-top:3px;background:rgba(16,22,30,.82);border-radius:8px;'
            + 'padding:4px 8px;max-height:8em;overflow:auto;white-space:pre;';
        det.textContent = [
            `braços: A4a=${bracos.reverse_confirm_arm ?? '-'} A4b=${bracos.free_pin_arm ?? '-'} T20=${bracos.early_confirm_arm ?? '-'} geo=${bracos.geo_arm ?? '-'}`,
            `pino por gatilho: ${resp} · texto: ${cont.textoPedidos}` + (ultimoAtendeu ? ` · ${ultimoAtendeu}` : ''),
            ultimoPino ? `confirmar: ${ultimoPino.confirm_reverse ?? '-'} · pulados ${ultimoPino.reverse_skipped_moves ?? '-'}` : 'confirmar: -',
            ...ultimos.slice(0, 6),
            'overrides: ' + JSON.stringify(ov) + '  (mpDebug.set(...) no console)',
        ].join('\n');
        el.appendChild(det);
    } catch (e) { /* painel nunca derruba a página */ }
}

// ---------- console ----------

function help(): string {
    const linhas = [
        'mpDebug — força A/B e configurações da tela de endereço NESTE aparelho (só fora de produção).',
        'Overrides ficam no localStorage e valem até mpDebug.reset(). Recarregue a página depois de mudar braço/dial.',
        'Toda telemetria emitida com override leva debug_overrides (as leituras excluem essas sessões).',
        '',
        'Comandos: mpDebug.help() · mpDebug.get() · mpDebug.set(chave, valor) · mpDebug.unset(chave) · mpDebug.reset() · mpDebug.clear() (zera o painel)',
        '',
        'Chaves:',
        ...(Object.keys(DEBUG_SPEC) as DebugKey[]).map((k) => `  ${k}: ${DEBUG_SPEC[k].descricao}\n     ex.: ${DEBUG_SPEC[k].exemplo}`),
        '',
        'O que a tela lê do cardapio.info (loja):',
        ...CARDAPIO_INFO_DA_TELA.map(([k, d]) => `  ${k}: ${d}`),
    ];
    const txt = linhas.join('\n');
    // eslint-disable-next-line no-console
    console.log(txt);
    return txt;
}

export function installDebugMode(win: any = typeof window !== 'undefined' ? window : null): boolean {
    if (!win || !debugEnabled()) return false;
    const api = {
        help,
        get: () => debugOverridesActive() || {},
        set: (key: DebugKey, value: any) => {
            const spec = DEBUG_SPEC[key];
            if (!spec) throw new Error(`mpDebug: chave desconhecida '${key}'. Veja mpDebug.help()`);
            const v = spec.normalizar(value);
            if (v === undefined) throw new Error(`mpDebug: valor inválido para '${key}'. ${spec.exemplo}`);
            const atual = (lerStore()?.overrides) || {};
            gravarStore({ ...atual, [key]: v });
            desenhar();
            return debugOverridesActive();
        },
        unset: (key: DebugKey) => {
            const atual = { ...((lerStore()?.overrides) || {}) };
            delete atual[key];
            gravarStore(atual);
            desenhar();
            return debugOverridesActive() || {};
        },
        reset: () => {
            gravarStore({});
            zerarContadores();
            desenhar();
            return {};
        },
        clear: () => {
            zerarContadores();
            desenhar();
        },
    };
    win.mpDebug = api;
    try {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => desenhar());
        else desenhar();
    } catch (e) { /* sem document: só o console */ }
    return true;
}
